import { useState, useEffect } from 'react'
import { currentEncounterNumber } from '../../utils/caseEncounters'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import {
  ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Calendar, Clock,
  Eye, Check, RefreshCw, Lock, Stethoscope, FileText, Info, CornerDownRight
} from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { aiAPI } from '../../services/api'
import { deliverNeedsOrellaMD } from '../../utils/caseGating'
import { diagnosisTextsOf, diagnosisCardOf } from '../../utils/diagnosisText'
import { asText, asFirst } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import {
  ENCOUNTER_CATEGORIES, STATUS_AMBER, getCardColor, normalizeCategory,
} from '../../config/encounterConfig'
import orellaLogoWhite from '../../assets/logos/orella_white_logo.png'
import { useHeroBox } from '../../hooks/useHeroBox' // PROD-1431 (RR1431 M1): the gradient box follows the real hero height

// ═══════════════════════════════════════════════════════
// CaseTimeline v218.0.7 — Unified Encounter-Chain
//
// ONE spine. ONE node per encounter. ONE status each.
// Replaces the two drifted renderers (hardcoded N=1 reading
// scattered case-level fields + separate N>=2 array iterator)
// with a single path: N=1 is encounters[0] like every N, with
// case-level patient_summary used only as the Describe overlay.
//
// Color is NEVER hand-picked — it is resolved by getCardColor
// (the single source of truth in encounterConfig). Observation
// matches MD Panel ObservationSection.jsx exactly (OBS_AMBER).
//
// Disposition = the connector pill that leads to N+1.
// Referral = a parallel fork node (its own case; links out).
// "Where things stand" = one tappable card → Deliver (the
// loop-completion surface) or Case Home (default).
// ═══════════════════════════════════════════════════════

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LIGHT_BLUE = '#A7D9FC'
const SURFACE = '#F8FAFB'
const LINE = '#E2E8F0'
const CARD_BD = '#E8EDF2'
const SLATE = '#94A3B8'

// PROD-1431 (R1431 M1): the hero is a pinned back-arrow row plus a body that scrolls away. HERO_H is
// the hero's height at rest with the synthetic fixture (169 px + the inset), the box both slices of
// the gradient are drawn in.
const HERO_GRAD = `linear-gradient(160deg, ${NAVY} 0%, #1E4A6E 60%, #245A80 100%)`
const HERO_ROW_H = 'calc(env(safe-area-inset-top, 0px) + 62px)'
const HERO_H = 'calc(env(safe-area-inset-top, 0px) + 169px)'
// RR1431 M1: HERO_H is only the first-paint fallback; useHeroBox writes the measured hero height to --hero-h.
// Observation palette — copied verbatim from MD ObservationSection.jsx (OBS_AMBER=#F59E0B)
const OBS = { color: '#F59E0B', light: '#FEF3C7', border: '#FDE68A', text: '#92500e' }
const GRAY = { color: '#94A3B8', light: '#F8FAFC', border: '#E2E8F0', text: '#64748B' }

// ─── Likelihood colors for Describe condition pills ───
const LIKELIHOOD = {
  High:     { bg: '#DCFCE7', text: '#16A34A', border: '#BBF7D0' },
  Moderate: { bg: '#FEF3C7', text: '#D97706', border: '#FDE68A' },
  Low:      { bg: '#F1F5F9', text: '#64748B', border: '#E2E8F0' },
}

// ─── AVS Parser (preserved verbatim — shared parsing logic) ───
const parseAVSIntoSections = (avsText, t) => {
  if (!avsText || typeof avsText !== 'string') return null
  const cleanText = avsText
    .replace(/[━═─┈┉┄┅╌╍│┃┆┇┊┋]/g, '')
    .replace(/^-{3,}\s*$/gm, '')
    .replace(/^[=*]{3,}\s*$/gm, '')
    .replace(/Questions\?\s*Try the Orella chat.*$/gm, '')
    .replace(/Thank you for choosing Orella.*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')

  const rawLines = cleanText.split('\n').map(l => l.trim()).filter(Boolean)
  const lines = []
  for (let i = 0; i < rawLines.length; i++) {
    const prev = lines.length > 0 ? lines[lines.length - 1] : ''
    const curr = rawLines[i]
    if (prev && /^[,;)}\].]/.test(curr)) { lines[lines.length - 1] = prev + curr; continue }
    lines.push(curr)
  }

  const knownHeaders = [
    /^(your\s+)?diagnosis/i, /^(what we found|examination|exam)\s*(findings|on)?/i,
    /^(your\s+)?treatment\s*(plan)?/i, /^care\s+instructions/i, /^medications?(\s+prescribed)?/i,
    /^(warning\s+signs|when\s+to\s+seek)/i, /^follow[\s\-]?up(\s+plan|\s+recommended)?/i,
    /^(before\s+(your\s+)?next|next\s+steps)/i, /^reason\s+for\s+(your\s+)?visit/i,
    /^procedures?(\s+performed)?/i,
  ]
  const isHeader = (line) => {
    const stripped = line.replace(/[:\-–—\d.#•*]/g, '').trim()
    if (!stripped || stripped.length < 3) return false
    if (stripped === stripped.toUpperCase() && /[A-Z]{3,}/.test(stripped)) return true
    if (stripped.length < 45 && knownHeaders.some(re => re.test(stripped))) return true
    return false
  }

  const rawSections = []
  let currentHeader = null, currentLines = []
  for (const line of lines) {
    if (isHeader(line)) {
      if (currentHeader && currentLines.length > 0) rawSections.push({ header: currentHeader, lines: [...currentLines] })
      else if (!currentHeader && currentLines.length > 0) rawSections.push({ header: '__INTRO__', lines: [...currentLines] })
      currentHeader = line.replace(/[:\s]+$/, '').trim()
      currentLines = []
    } else { currentLines.push(line) }
  }
  if (currentHeader && currentLines.length > 0) rawSections.push({ header: currentHeader, lines: currentLines })
  if (rawSections.length === 0) return null

  const headerMap = [
    { match: /REASON\s+FOR|YOUR\s+VISIT|CHIEF\s+COMPLAINT/i, title: t('caseTimeline.reasonForYourVisit'), color: 'navy' },
    { match: /DIAGNOSIS|YOUR DIAGNOSIS/i, title: t('caseTimeline.yourDiagnosis'), color: 'navy' },
    { match: /WHAT WE FOUND|EXAMINATION|EXAM FINDINGS/i, title: t('caseTimeline.examinationFindings'), color: 'gray' },
    { match: /TREATMENT|CARE PLAN/i, title: t('caseTimeline.treatmentPlan'), color: 'green' },
    { match: /CARE\s+INSTRUCTIONS/i, title: t('caseTimeline.careInstructions'), color: 'blue' },
    { match: /MEDICATIONS?/i, title: t('caseTimeline.medications'), color: 'green' },
    { match: /WARNING|WHEN TO SEEK/i, title: t('caseTimeline.warningSigns'), color: 'red' },
    { match: /FOLLOW[\s\-]?UP/i, title: t('caseTimeline.followupPlan'), color: 'blue' },
    { match: /BEFORE\s+(YOUR|NEXT)|NEXT\s+STEPS/i, title: t('caseTimeline.nextSteps'), color: 'blue' },
    { match: /PROCEDURES?/i, title: t('caseTimeline.procedures'), color: 'gray' },
  ]

  return rawSections.map(s => {
    if (s.header === '__INTRO__') return { title: t('caseTimeline.summary'), color: 'navy', content: s.lines }
    const mapped = headerMap.find(h => h.match.test(s.header))
    return mapped
      ? { title: mapped.title, color: mapped.color, content: s.lines }
      : { title: s.header.replace(/\b\w/g, c => c.toUpperCase()).substring(0, 40), color: 'gray', content: s.lines }
  })
}

const safeText = (v) => {
  if (!v) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'object' && v.avsContent) return String(v.avsContent)
  return typeof v === 'object' ? JSON.stringify(v) : String(v)
}

// ─── Sub-components ───

function SectionCard({ title, borderColor, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: `${borderColor}30` }}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3.5 text-left" style={{ backgroundColor: `${borderColor}08` }}>
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: borderColor }} />
          <span className="text-sm font-bold" style={{ color: NAVY }}>{title}</span>
        </div>
        {open ? <ChevronDown size={16} style={{ color: borderColor }} /> : <ChevronRight size={16} style={{ color: borderColor }} />}
      </button>
      {open && <div className="p-3.5 pt-1 space-y-2">{children}</div>}
    </div>
  )
}

function ConditionPill({ name, likelihood, rationale }) {
  const lc = LIKELIHOOD[likelihood] || LIKELIHOOD.Low
  const [expanded, setExpanded] = useState(false)
  const displayName = typeof name === 'string' ? name : (name?.name || name?.diagnosis || name?.condition || String(name || ''))
  return (
    <button onClick={() => rationale && setExpanded(!expanded)} className="w-full text-left rounded-xl overflow-hidden" style={{ border: `1.5px solid ${lc.border}`, backgroundColor: lc.bg }}>
      <div className="flex items-center gap-2 p-2.5">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: lc.text }} />
        <span className="text-xs font-semibold flex-1" style={{ color: lc.text }}>{displayName}</span>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: lc.text }}>{likelihood}</span>
      </div>
      {expanded && rationale && (
        <div className="px-3 pb-2.5 pt-0">
          <p className="text-[11px] leading-relaxed" style={{ color: lc.text, opacity: 0.8 }}>{rationale}</p>
        </div>
      )}
    </button>
  )
}

// v225.14.0 (#12): a single confirmed diagnosis (Diagnose phase = navy). Replaces the
// ranked "possible conditions" pills once the encounter is completed and the MD has
// confirmed a diagnosis.
// R14 / PROD-260 (patient P1): the diagnosis TEXT only. The ICD-10 code chip beside it, and
// the fallback that printed the bare code when there was no text, are gone.
function ConfirmedDxPill({ description }) {
  return (
    <div className="w-full rounded-xl overflow-hidden" style={{ border: `1.5px solid ${NAVY}2E`, backgroundColor: `${NAVY}0A` }}>
      <div className="flex items-center gap-2 p-2.5">
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: NAVY }} />
        <span className="text-xs font-semibold flex-1" style={{ color: NAVY }}>{description}</span>
      </div>
    </div>
  )
}

// Timeline spine dot
function Dot({ color, children, pulse = false }) {
  return (
    <div className="absolute -left-[36px] top-0 w-[30px] h-[30px] rounded-full flex items-center justify-center text-white text-xs font-bold"
      style={{ backgroundColor: color, border: `3px solid ${SURFACE}`,
        boxShadow: pulse ? `0 0 0 0 ${color}55` : 'none',
        animation: pulse ? 'ct-pulse 1.8s ease-in-out infinite' : 'none' }}>
      {children}
    </div>
  )
}

// One encounter node — color ALWAYS via getCardColor (or OBS for observation)
function EncounterNode({ palette, dotInner, title, statusLabel, sub, live,
  insetIcon, insetText, params, footerAction, active, pulse, children }) {
  return (
    <div className="relative mb-[7px]">
      <Dot color={palette.color} pulse={pulse}>{dotInner}</Dot>
      <div className={`bg-white rounded-[15px] ${footerAction ? 'cursor-pointer ct-tappable' : ''}`}
        style={{ border: `${active ? '2px' : '1px'} solid ${active ? palette.color : CARD_BD}`, padding: '14px 15px' }}
        onClick={footerAction ? footerAction.onClick : undefined}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[14.5px] font-semibold" style={{ color: NAVY }}>{title}</span>
          <span className="text-[10px] font-semibold px-2.5 py-[3px] rounded-full inline-flex items-center gap-[5px] whitespace-nowrap"
            style={{ color: palette.text, background: palette.light, border: `1px solid ${palette.border}` }}>
            {live && <span className="w-[5px] h-[5px] rounded-full" style={{ background: palette.color, animation: 'ct-blink 1.4s ease-in-out infinite' }} />}
            {statusLabel}
          </span>
        </div>
        <div className="text-[11px] mb-2.5" style={{ color: SLATE }}>{sub}</div>
        {insetText && (
          <div className="flex items-start gap-2 px-[11px] py-[9px] rounded-[10px] text-[11.5px] leading-snug"
            style={{ background: palette.light, border: `1px solid ${palette.border}`, color: palette.text }}>
            {insetIcon}<span>{insetText}</span>
          </div>
        )}
        {params && (
          <div className="mt-[9px] rounded-[10px] overflow-hidden" style={{ border: `1px solid ${OBS.border}` }}>
            <div className="flex items-center gap-1.5 px-[11px] py-[7px] text-[10px] font-bold uppercase tracking-wide"
              style={{ background: OBS.light, color: OBS.text }}>
              <Clock size={12} />{params.title}
            </div>
            {params.rows.map(([k, v], i) => (
              <div key={i} className="flex justify-between gap-2.5 px-[11px] py-[7px] text-[11.5px]"
                style={{ borderTop: i === 0 ? 'none' : `1px solid ${OBS.light}` }}>
                <span style={{ color: SLATE }}>{k}</span>
                <span className="font-medium text-right" style={{ color: NAVY }}>{v}</span>
              </div>
            ))}
          </div>
        )}
        {children}
        {footerAction && (
          <div className="flex items-center justify-between mt-[11px] pt-[11px]" style={{ borderTop: `1px solid #EEF1F4` }}>
            <span className="text-[12px] font-semibold" style={{ color: footerAction.color || palette.color }}>{footerAction.label}</span>
            <ArrowRight size={15} style={{ color: footerAction.color || palette.color }} />
          </div>
        )}
      </div>
    </div>
  )
}

// Disposition / referral connector pill
function Connector({ kind, label }) {
  const map = {
    follow:  { c: getCardColor('confirmed', 'followUp'), Icon: RefreshCw },
    close:   { c: { text: '#166534', light: '#F0FDF4', border: '#BBF7D0' }, Icon: Check },
    obs:     { c: { text: OBS.text, light: OBS.light, border: OBS.border }, Icon: Eye },
    ref:     { c: getCardColor('confirmed', 'referral'), Icon: CornerDownRight },
  }
  const m = map[kind] || map.follow
  return (
    <div className="py-[7px] pl-0.5">
      <span className="inline-flex items-center gap-[7px] text-[10.5px] font-medium px-3 py-[5px] rounded-full"
        style={{ color: m.c.text, background: m.c.light, border: `1px solid ${m.c.border}` }}>
        <m.Icon size={12} />{label}
      </span>
    </div>
  )
}

// "Where things stand" — uniform, whole-node tappable, one destination
function WhereThingsStand({ accent, now, waiting, dest }) {
  const { t } = useI18n()
  return (
    <div className="relative mb-[7px]">
      <Dot color={NAVY}><Clock size={14} /></Dot>
      <div className="bg-white rounded-[15px] overflow-hidden cursor-pointer ct-tappable"
        style={{ border: `1px solid ${CARD_BD}` }} onClick={dest.onClick}>
        <div className="flex">
          <div className="w-1 flex-shrink-0" style={{ background: accent }} />
          <div className="flex-1" style={{ padding: '14px 15px' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[14.5px] font-semibold" style={{ color: NAVY }}>{t('caseTimeline.whereThingsStand')}</span>
              <span className="text-[10px] font-bold tracking-wide px-2.5 py-[3px] rounded-full text-white"
                style={{ background: NAVY }}>{t('caseTimeline.now')}</span>
            </div>
            <p className="text-[12px] leading-relaxed mt-[3px]" style={{ color: NAVY }}>{now}</p>
            <div className="flex items-start gap-[7px] mt-[9px] px-[11px] py-[9px] rounded-[9px] text-[11px] leading-snug"
              style={{ background: '#F4F6F8', color: '#475569' }}>
              <Clock size={13} className="flex-shrink-0 mt-0.5" />
              <span><b style={{ color: NAVY }}>{t('caseTimeline.waitingOn')}:</b> {waiting}</span>
            </div>
            <div className="flex items-center justify-between mt-[11px] pt-[11px]" style={{ borderTop: '1px solid #EEF1F4' }}>
              <span className="text-[12px] font-semibold" style={{ color: accent }}>{dest.label}</span>
              <ArrowRight size={15} style={{ color: accent }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════

export default function CaseTimeline() {
  const navigate = useNavigate()
  const [heroRow, heroBody] = useHeroBox() // PROD-1431 (RR1431 M1)
  const location = useLocation()
  const { currentCase, updateCase } = useCaseStore()
  const { t, language } = useI18n()

  const [narrative, setNarrative] = useState('')
  const [dxList, setDxList] = useState([])
  const [loaded, setLoaded] = useState(false)

  // v218.0.7.1: Scroll to top when the timeline opens. The mount-only reset
  // raced the async summary fetch (same class as the v218.0.4 My Visits bug):
  // it fired while the page was short, then the summary landed, the spine grew,
  // and nothing re-scrolled — so opening from Case Home's "Case Encounters"
  // button could land scrolled down. Re-assert on mount + a short delayed pass
  // for post-render reflow. Dedicated effect (scroll decoupled from data load).
  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo({ top: 0, behavior: 'instant' })
      const sw = document.querySelector('.scroll-wrapper')
      if (sw) sw.scrollTop = 0
    }
    resetScroll()
    const t1 = setTimeout(resetScroll, 100)
    const t2 = setTimeout(resetScroll, 300)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [currentCase?.id])

  // Load cached patient summary (the Describe checkpoint) — never regenerate.
  // Preserved verbatim from prior implementation: cached store → describeSummary
  // → preVisitSummary → server cached fetch (which persists, never regenerates).
  useEffect(() => {
    if (!currentCase || loaded) return
    setLoaded(true)

    const saved = currentCase.patientSummary || currentCase.patient_summary
    if (saved && (saved.isUnifiedPVS || saved.conditionsToDiscuss?.length > 0 || saved.differentialDx?.length > 0)) {
      setNarrative(saved.patientNarrative || saved.summary || '')
      setDxList(saved.differentialDx || saved.conditionsToDiscuss || [])
    } else if (currentCase.describeSummary) {
      setNarrative(currentCase.describeSummary)
    } else if (currentCase.preVisitSummary) {
      const pvs = typeof currentCase.preVisitSummary === 'string'
        ? (() => { try { return JSON.parse(currentCase.preVisitSummary) } catch { return null } })()
        : currentCase.preVisitSummary
      if (pvs) {
        setNarrative(pvs.patientNarrative || pvs.summary || '')
        setDxList(pvs.differentialDx || pvs.conditionsToDiscuss || [])
      }
    }

    if (!narrative && currentCase.id) {
      aiAPI.generatePatientSummary(currentCase.id, false, language)
        .then(result => {
          if (result) {
            setNarrative(prev => prev || result.patientNarrative || result.summary || '')
            setDxList(prev => prev.length > 0 ? prev : (result.differentialDx || result.conditionsToDiscuss || []))
            updateCase({ patientSummary: result, patient_summary: result })
          }
        })
        .catch(err => console.log('[CaseTimeline] Summary fetch failed:', err?.message))
    }
  }, [currentCase?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  if (!currentCase) return <Navigate to="/home" replace />

  // ─── Derived data ───
  const specName = currentCase.specialtyName || currentCase.specialty?.name || ''
  const focusName = currentCase.focusAreaName || currentCase.focusArea?.name || ''
  const caseId = currentCase.id?.slice(-8)?.toUpperCase() || ''
  const startDate = currentCase.createdAt || currentCase.created_at
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' }) : ''
  const formattedStart = fmtDate(startDate)

  const encounters = currentCase.encounters || []
  // v225.14.0 (#12): confirmed diagnosis (ICD-10) from the finalized consult note —
  // same source MyMedicalNote reads. When the initial encounter is completed AND the
  // MD has confirmed structured diagnoses, the "Possible Conditions" checkpoint becomes
  // "Diagnosis" and collapses to the confirmed dx(es) instead of the pre-visit differential.
  const billingCodes = (() => {
    let fin = null
    const raw = currentCase.avsContent || currentCase.afterVisitSummary || currentCase.diagnose?.avsContent || ''
    if (raw) { try { const p = JSON.parse(raw); if (p && typeof p === 'object') fin = p } catch { /* not JSON */ } }
    return (fin && fin.billingCodes) || currentCase.diagnose?.billingCodes || currentCase.billingCodes || []
  })()
  // R14 / PROD-260: the diagnosis texts, never a raw ICD-10 code (utils/diagnosisText.js)
  const confirmedDx = diagnosisTextsOf(billingCodes)
  const currentEncNum = currentEncounterNumber(currentCase)
  const status = currentCase.status || ''
  const isFinalized = ['completed', 'in_follow_up', 'in_observation'].includes(status) ||
    !!(currentCase.avsContent || currentCase.afterVisitSummary || currentCase.diagnose?.avsContent)
  // ★ PROD-593: the confirmed diagnosis, or nothing when the finalized codes carry no text; the
  //   pre-visit AI "possible conditions" only before that (utils/diagnosisText.js diagnosisCardOf)
  const dxCard = diagnosisCardOf(billingCodes, isFinalized, dxList.length)
  const followUp = currentCase.followUp || currentCase.diagnose?.followUp || {}
  const disposition = followUp.type || followUp.followUpPlan || (status === 'completed' ? 'complete' : null)
  const referrals = currentCase.referrals || []
  const followUpReferral = followUp.referral || null
  const drName = currentCase.doctor?.name || currentCase.doctorName || currentCase.assignedDoctorName || ''

  // ─── Navigation ───
  // v234.16.0: MD-agnostic cases have no managed Deliver — route Deliver/Reopen actions to
  // Case Home and suppress their CTAs (the DeliverScreen guard is the backstop either way).
  const deliverGated = deliverNeedsOrellaMD(currentCase)
  const goCaseHome = () => navigate('/case/home')
  const goDeliver = () => navigate(deliverGated ? '/case/home' : '/case/deliver')
  const goReopen = () => deliverGated ? navigate('/case/home') : navigate('/case/deliver', { state: { reopen: true } })
  const goDiagnose = () => navigate('/case/diagnose')
  const goReferralCase = () => navigate('/cases') // no spawned-id on referral obj; list is the safe entry

  const goBack = () => {
    if (isFinalized || location.state?.from === 'caseHome') return '/case/home'
    return '/case/describe/review'
  }

  // ─── Build the unified spine ───
  // N=1 (initial) is treated as the first node. Its Describe overlay comes
  // from the case-level patient_summary checkpoint (its legitimate special
  // role). All structured clinical content for every N comes from the same
  // encounter shape. Color is ALWAYS getCardColor — never hand-picked.

  const initialPalette = (() => {
    if (status === 'completed' || isFinalized) {
      const c = getCardColor('completed', 'followUp'); return { color: c.primary, light: c.light, border: c.border, text: c.text }
    }
    // in-progress / pre-finalize → initial category color
    const c = getCardColor('in_progress', 'initial')
    return { color: c.primary, light: c.light, border: c.border, text: c.text }
  })()

  const hasDescribeData = narrative || dxList.length > 0
  const initialActive = !isFinalized

  // Disposition connector kind
  const dispoKind =
    (disposition === 'observation') ? 'obs' :
    (['complete', 'completed', 'closed'].includes(disposition)) ? 'close' :
    (disposition ? 'follow' : null)
  const dispoLabel =
    dispoKind === 'obs' ? t('caseTimeline.connectorObservation') :
    dispoKind === 'close' ? t('caseTimeline.connectorClosed') :
    dispoKind === 'follow' ? t('caseTimeline.connectorFollowup') : ''

  // Encounter 2+ rows (unified shape)
  const laterEncounters = encounters
    .filter(e => (e.encounter_number || e.encounterNumber) >= 2)
    .sort((a, b) => (a.encounter_number || a.encounterNumber) - (b.encounter_number || b.encounterNumber))

  // Referral fork node (parallel branch — links to its own case)
  const refSpecialty = asFirst(followUpReferral?.specialty,
    (referrals.find(r => (r.source_encounter || r.sourceEncounter) === 1) || {}).specialty,
    (referrals[0] || {}).specialty, (referrals[0] || {}).target_specialty) // PROD-1156
  const hasReferral = isFinalized && !!refSpecialty

  // "Where things stand" — about THIS case only (never the referral branch)
  const wts = (() => {
    if (status === 'completed' || ['complete', 'completed', 'closed'].includes(disposition)) return null // terminal: no node
    if (disposition === 'observation' || status === 'in_observation') {
      const iv = asFirst(followUp.observationInterval, followUp.interval) // PROD-1156
      return {
        accent: OBS.color,
        now: iv
          ? t('caseTimeline.wtsObservationNowInterval', { interval: iv })
          : t('caseTimeline.wtsObservationNow'),
        waiting: t('caseTimeline.wtsObservationWaiting'),
        dest: deliverGated ? undefined : { label: t('caseTimeline.goToDeliver'), onClick: goDeliver },
      }
    }
    if (disposition && status === 'in_follow_up') {
      return {
        accent: BLUE,
        now: t('caseTimeline.wtsFollowupNow'),
        waiting: t('caseTimeline.wtsFollowupWaiting'),
        dest: deliverGated ? undefined : { label: t('caseTimeline.goToDeliver'), onClick: goDeliver },
      }
    }
    // pre-finalize / degraded (waiting on summary + MD) → Case Home default
    return {
      accent: BLUE,
      now: hasDescribeData ? t('caseTimeline.wtsReviewNow') : t('caseTimeline.wtsPreparingNow'),
      waiting: hasDescribeData ? t('caseTimeline.wtsReviewWaiting') : t('caseTimeline.wtsPreparingWaiting'),
      dest: { label: t('caseTimeline.goToCaseHome'), onClick: goCaseHome },
    }
  })()

  return (
    <div className="min-h-full flex flex-col" style={{ background: SURFACE }}>
      <style>{`
        @keyframes ct-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,.45)} 50%{box-shadow:0 0 0 6px rgba(245,158,11,0)} }
        @keyframes ct-blink { 0%,100%{opacity:1} 50%{opacity:.35} }
        .ct-tappable { transition: transform .12s ease, box-shadow .12s ease; }
        .ct-tappable:active { transform: scale(.985); box-shadow: 0 2px 10px rgba(21,53,79,.08); }
      `}</style>

      {/* ═══ HEADER ═══ PROD-1431 (R1431 M1): only the back-arrow row is pinned at the top; its navy
          top padding is the status-bar band. The body below it scrolls away. Both paint slices of one
          gradient box, as tall as the measured hero (useHeroBox), so at rest the hero reads as one piece. */}
      <div className="sticky-inset-header px-[18px] flex-shrink-0 overflow-hidden"
        ref={heroRow} style={{ background: `${HERO_GRAD} 0 0 / 100% var(--hero-h, ${HERO_H}) no-repeat`, height: HERO_ROW_H, paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
        <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full" style={{ background: 'rgba(70,168,249,0.08)' }} />
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(goBack())} className="p-1.5 -ml-1.5"><ArrowLeft size={22} className="text-white" /></button>
          <img src={orellaLogoWhite} alt="Orella" className="h-[18px] object-contain opacity-70" />
        </div>
      </div>
      <div className="relative overflow-hidden flex-shrink-0 px-[18px] pb-5"
        ref={heroBody} style={{ background: `${HERO_GRAD} 0 calc(-1 * ${HERO_ROW_H}) / 100% var(--hero-h, ${HERO_H}) no-repeat, #245A80` }}>
        <div className="absolute -right-10 w-36 h-36 rounded-full" style={{ top: `calc(-40px - ${HERO_ROW_H})`, background: 'rgba(70,168,249,0.08)' }} />
        <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[1.5px] px-3 py-1 rounded-full border mb-3"
          style={{ color: `${LIGHT_BLUE}E6`, backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.12)' }}>
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: LIGHT_BLUE }} />
          {t('caseTimeline.careJourney')}
        </div>
        <h1 className="text-xl font-bold text-white">Case #{caseId}</h1>
        <p className="text-[12px] text-white/40 mt-0.5">
          {specName}{focusName ? ` › ${focusName}` : ''}{formattedStart ? ` · ${t('caseTimeline.started')} ${formattedStart}` : ''}
        </p>
      </div>

      {/* ═══ SPINE ═══ */}
      <div className="flex-1 overflow-auto px-4 py-5">
        <div className="relative pl-9">
          <div className="absolute left-[14px] top-4 bottom-4 w-0.5" style={{ background: LINE }} />

          {/* ── ENCOUNTER 1 (initial) ── */}
          <EncounterNode
            palette={initialPalette}
            dotInner={(status === 'completed' || isFinalized) ? <Check size={14} /> : '1'}
            title={t('caseTimeline.initialConsult')}
            statusLabel={(status === 'completed' || isFinalized) ? t('caseTimeline.statusCompleted') : t('caseTimeline.statusInProgress')}
            sub={`${t('caseTimeline.encounter')} 1${formattedStart ? ` · ${formattedStart}` : ''}${drName ? ` · ${drName}` : ''}`}
            live={initialActive}
            active={initialActive}
            insetIcon={hasDescribeData ? <Stethoscope size={14} className="flex-shrink-0" /> : <Info size={14} className="flex-shrink-0" />}
            insetText={!hasDescribeData ? t('caseTimeline.summaryBeingPrepared') : undefined}
          >
            {/* Describe overlay — narrative + conditions (the checkpoint) */}
            {(narrative || dxList.length > 0 || dxCard === 'diagnosis') && (
              <div className="space-y-2.5 mt-2.5">
                {narrative && (
                  <SectionCard title={t('caseTimeline.yourStory')} borderColor={LIGHT_BLUE} defaultOpen={false}>
                    <p className="text-xs text-gray-600 leading-relaxed">{narrative}</p>
                  </SectionCard>
                )}
                {dxCard === 'diagnosis' ? (
                  <SectionCard title={t('caseTimeline.diagnosis')} borderColor={NAVY} defaultOpen={true}>
                    <div className="space-y-1.5">
                      {confirmedDx.map((dx, i) => (
                        <ConfirmedDxPill key={i} description={dx} />
                      ))}
                    </div>
                  </SectionCard>
                ) : dxCard === 'possible' && (
                  <SectionCard title={t('caseTimeline.possibleConditions')} borderColor={LIGHT_BLUE} defaultOpen={true}>
                    <div className="space-y-1.5">
                      {dxList.map((dx, i) => (
                        <ConditionPill key={i}
                          name={typeof dx === 'string' ? dx : (dx.diagnosis || dx.name || dx.condition || JSON.stringify(dx))}
                          likelihood={dx.likelihood || 'Low'}
                          rationale={dx.reasoning || dx.rationale || ''} />
                      ))}
                    </div>
                  </SectionCard>
                )}
                {isFinalized && (
                  <button onClick={goDiagnose}
                    className="w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-gray-100"
                    style={{ backgroundColor: `${NAVY}0F`, color: NAVY, border: `1px solid ${NAVY}1F` }}>
                    <FileText size={13} /> {t('caseTimeline.viewVisitSummary')} <ChevronRight size={13} />
                  </button>
                )}
              </div>
            )}
          </EncounterNode>

          {/* ── Disposition connector (N → N+1) ── */}
          {isFinalized && dispoKind && <Connector kind={dispoKind} label={dispoLabel} />}

          {/* ── ENCOUNTER 2+ (unified shape) ── */}
          {laterEncounters.map((enc) => {
            const encNum = enc.encounter_number || enc.encounterNumber
            const rawCat = enc.category || ''
            const cat = normalizeCategory(rawCat) || 'followUp'
            const isObs = rawCat === 'observation' || enc.is_observation
            const isTerminal = ['complete', 'completed', 'closed'].includes(rawCat) || enc.terminal
            const encFinalized = enc.finalized
            const encAvs = safeText(enc.after_visit_summary || enc.afterVisitSummary || '')
            const carePlan = typeof enc.care_plan === 'string'
              ? (() => { try { return JSON.parse(enc.care_plan) } catch { return {} } })()
              : (enc.care_plan || {})

            // Color ALWAYS via resolver (or OBS for observation, matching MD)
            let palette
            if (isObs) palette = OBS
            else if (isTerminal) palette = GRAY
            else {
              const encStatus = encFinalized ? 'completed' : (enc.status || 'confirmed')
              const c = getCardColor(encStatus, cat)
              palette = { color: c.primary, light: c.light, border: c.border, text: c.text }
            }

            const titleKey = isObs ? 'caseTimeline.observationPeriod'
              : isTerminal ? 'caseTimeline.careComplete'
              : 'caseTimeline.followupVisit'
            const statusKey = isObs ? 'caseTimeline.statusInObservation'
              : isTerminal ? 'caseTimeline.statusClosed'
              : encFinalized ? 'caseTimeline.statusCompleted' : 'caseTimeline.statusConfirmed'

            const obsParams = isObs ? {
              title: t('caseTimeline.observationParameters'),
              rows: [
                // ★ PROD-1156: each value is drawn as text (utils/asText.js), never as an object.
                [t('caseTimeline.obsDuration'), asFirst(carePlan.observationInterval, followUp.observationInterval) || '—'],
                [t('caseTimeline.obsCheckins'), asText(carePlan.checkInSchedule) || '—'],
                [t('caseTimeline.obsWatchFor'), asFirst(carePlan.escalationCriteria, carePlan.observationNotes) || '—'],
                [t('caseTimeline.obsIfTriggered'), t('caseTimeline.obsEscalates')],
              ],
            } : null

            return (
              <EncounterNode key={encNum}
                palette={palette}
                dotInner={isTerminal ? <Check size={14} /> : String(encNum)}
                title={t(titleKey)}
                statusLabel={t(statusKey)}
                sub={`${t('caseTimeline.encounter')} ${encNum}${enc.created_at ? ` · ${fmtDate(enc.created_at)}` : ''}`}
                live={isObs || (!isTerminal && encNum === currentEncNum)}
                pulse={isObs}
                active={isObs || (!isTerminal && encNum === currentEncNum)}
                insetIcon={isObs ? <Eye size={14} className="flex-shrink-0" /> : isTerminal ? <Check size={14} className="flex-shrink-0" /> : <Calendar size={14} className="flex-shrink-0" />}
                insetText={isObs ? t('caseTimeline.obsInsetText')
                  : isTerminal ? t('caseTimeline.terminalInsetText')
                  : t('caseTimeline.followupInsetText')}
                params={obsParams}
                footerAction={isTerminal && !deliverGated ? { label: t('caseTimeline.reopenCase'), color: NAVY, onClick: goReopen } : undefined}
              >
                {!isObs && !isTerminal && encAvs && (
                  <div className="mt-2.5">
                    <SectionCard title={t('caseTimeline.visitSummary')} borderColor={NAVY} defaultOpen={false}>
                      <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {encAvs.substring(0, 300)}{encAvs.length > 300 ? '…' : ''}
                      </p>
                      <button onClick={goDiagnose}
                        className="mt-2 w-full py-2 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5"
                        style={{ backgroundColor: `${NAVY}0F`, color: NAVY, border: `1px solid ${NAVY}1F` }}>
                        <FileText size={13} /> {t('caseTimeline.viewVisitSummary')} <ChevronRight size={13} />
                      </button>
                    </SectionCard>
                  </div>
                )}
              </EncounterNode>
            )
          })}

          {/* ── Referral fork (parallel branch — its own case) ── */}
          {hasReferral && (
            <>
              <Connector kind="ref" label={t('caseTimeline.connectorReferral')} />
              <EncounterNode
                palette={(() => { const c = getCardColor('confirmed', 'referral'); return { color: c.primary, light: c.light, border: c.border, text: c.text } })()}
                dotInner={<CornerDownRight size={14} />}
                title={t('caseTimeline.referral')}
                statusLabel={t('caseTimeline.statusActionNeeded')}
                sub={`${t('caseTimeline.createdFromEncounter')} 1${refSpecialty ? ` · ${refSpecialty}` : ''}`}
                insetIcon={<Info size={14} className="flex-shrink-0" />}
                insetText={t('caseTimeline.referralInsetText')}
                footerAction={{ label: t('caseTimeline.openReferralCase'), onClick: goReferralCase }}
              />
            </>
          )}

          {/* ── Where things stand (about THIS case only) ── */}
          {wts && <WhereThingsStand {...wts} />}

          <div className="h-4" />
        </div>
      </div>

      {/* ═══ FOOTER ═══ */}
      <div className="p-4 pb-5 flex-shrink-0 bg-white border-t border-gray-100">
        <button onClick={() => navigate(goBack())}
          className="w-full py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2"
          style={{ backgroundColor: '#F1F5F9', color: '#64748B' }}>
          <ArrowLeft size={16} /> {t('caseTimeline.backToCaseHome')}
        </button>
      </div>
    </div>
  )
}
